import {type ComputedRef, isRef} from 'vue';
import dayjs from "dayjs";

interface FormatOptions {
    currency?: string;
    locale?: string;
    decimals?: number;
}

export function useFormatting() {
    const defaultOptions: FormatOptions = {
        currency: 'EUR',
        locale: 'nl-BE',
        decimals: 2
    };

    const createFormatter = (options: FormatOptions = {}) => {
        const { currency, locale, decimals } = { ...defaultOptions, ...options };

        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };
    
    const roundToCurrency = (value: number): number => {
        const scaled = value * 100;
        const correction = Number.EPSILON * Math.abs(scaled);
        const adjusted = scaled >= 0 ? scaled + correction : scaled - correction;
        const rounded = adjusted >= 0 ? Math.round(adjusted) : -Math.round(-adjusted);
        return rounded / 100;
    };

    const formatCurrency = (
        value: number | ComputedRef<number | undefined> | undefined,
        options?: FormatOptions
    ): string => {
        const amount = isRef(value) ? (value.value ?? 0) : (value ?? 0);
        return createFormatter(options).format(amount);
    };


    const formatDate = (
        date: Date | string | number | undefined,
        format: string = 'DD/MM/YYYY'
    ): string => {
        if (!date) return '';

        const parsed = dayjs(date);
        return parsed.isValid() ? parsed.format(format) : '';
    };

    const formatDateTime = (
        date: Date | string | number | undefined,
        format: string = 'DD/MM/YYYY HH:mm:ss'
    ): string => {
        if (!date) return '';

        const parsed = dayjs(date);
        return parsed.isValid() ? parsed.format(format) : '';
    }

    return {
        formatCurrency,
        formatDate,
        formatDateTime,
        roundToCurrency
    };
}
